import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  MdAutoAwesome,
  MdCheckCircle,
  MdDelete,
  MdDownload,
  MdUploadFile,
} from 'react-icons/md';
import { useAuthHttp } from '@hooks';
import { MASTER_DATA_IMPORT_CREATE, UPLOAD_SKU_MASTER_URL } from '@constants';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';
import { Dropzone } from '@tailwindComponents/Dropzone';
import { showAiFeaturesAtom } from '../../../store/flags';
import { isUploadSKUsModalOpenAtom } from '../../../store/jotai';

export interface SkuUploadModalProps {
  onUploaded: () => void;
}

interface UploadSummary {
  numberOfSkusCreated: number;
  numberOfExistingSkusUpdatedWithImages: number;
  numberOfExistingSkusSkipped: number;
  numberOfImagesImported: number;
}

export function SkuUploadModal({ onUploaded }: SkuUploadModalProps) {
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const navigate = useNavigate();
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);
  const [isUploadSKUsModalOpen, setUploadSKUsModalOpen] = useAtom(
    isUploadSKUsModalOpenAtom
  );
  const [uploadFile, setUploadFile] = useState<any>(undefined);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isAiImportLoading, setIsAiImportLoading] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<
    { message: string; row: number }[]
  >([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | undefined>(
    undefined
  );

  const closeModal = () => {
    setUploadFile(undefined);
    setHasUploadError(false);
    setUploadErrors([]);
    setUploadErrorMessage(null);
    setUploadSummary(undefined);
    setUploadSKUsModalOpen(false);
  };

  const handleAiImport = async () => {
    try {
      setIsAiImportLoading(true);
      const { data } = await authHttpFormData.post(
        MASTER_DATA_IMPORT_CREATE,
        {
          importFile: uploadFile,
          entityType: 'InventorySku',
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data?.importId) {
        closeModal();
        navigate(`/master-data/imports/${data.importId}`);
      }
    } catch (error: any) {
      setHasUploadError(true);
      setUploadErrors([]);
      setUploadErrorMessage(
        error?.response?.data?.message ??
          'The file could not be uploaded for AI review.'
      );
    } finally {
      setIsAiImportLoading(false);
    }
  };

  const handleUploadSKUExcel = async () => {
    try {
      setIsUploadLoading(true);
      const { data } = await authHttpFormData.post(
        UPLOAD_SKU_MASTER_URL,
        {
          skusFile: uploadFile,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data) {
        setHasUploadError(false);
        setUploadErrors([]);
        setUploadSummary({
          numberOfSkusCreated: data.numberOfSkusCreated ?? 0,
          numberOfExistingSkusUpdatedWithImages:
            data.numberOfExistingSkusUpdatedWithImages ?? 0,
          numberOfExistingSkusSkipped: data.numberOfExistingSkusSkipped ?? 0,
          numberOfImagesImported: data.numberOfImagesImported ?? 0,
        });
        onUploaded();
      }
    } catch (error: any) {
      const errorResponse = error as any;
      setHasUploadError(true);
      setUploadErrors(errorResponse?.response?.data?.errors ?? []);
    } finally {
      setIsUploadLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isUploadSKUsModalOpen}
      setIsOpen={(open) => (open ? setUploadSKUsModalOpen(true) : closeModal())}
      title='Upload products'
    >
      <div className='flex flex-col items-center justify-center gap-8 p-6 min-h-[400px]'>
        {uploadSummary ? (
          <UploadSuccessPanel summary={uploadSummary} onDone={closeModal} />
        ) : (
          <>
            <div className='flex w-full flex-col gap-2'>
              <a
                href='/files/product_import_template.xlsx'
                className='w-full'
                download
              >
                <Button
                  intent='primary'
                  label='Download product template'
                  size='sm'
                  startIcon={<MdDownload size='1.5rem' />}
                  className='w-full'
                />
              </a>
              <p className='text-center text-xs text-gray-500'>
                One row per product — FLOX creates a SKU for each level you fill
                in. Paste a photo into the SKU Image column to set its image.
              </p>
              <a
                href='/files/sku_master_template.xlsx'
                className='w-full text-center text-xs text-gray-500 underline hover:text-primary'
                download
              >
                Advanced: SKU-level template (one row per SKU)
              </a>
            </div>
            <div className='font-semibold text-gray-700'>OR</div>
            {uploadFile ? (
              <div className='flex flex-col gap-6 w-full items-center'>
                <div className='flex flex-row gap-2 items-center'>
                  <MdUploadFile size='1.5rem' />
                  <span className='font-semibold text-base'>
                    {uploadFile.name}
                  </span>
                </div>
                {showAiFeatures ? (
                  <div className='flex w-full flex-col items-center gap-1'>
                    <Button
                      intent='primary'
                      label={
                        isAiImportLoading
                          ? 'Uploading...'
                          : 'Import with AI review'
                      }
                      startIcon={<MdAutoAwesome />}
                      onClick={handleAiImport}
                      disabled={isAiImportLoading}
                    />
                    <p className='text-center text-xs text-gray-500'>
                      Wingman analyses the file — you review and approve before
                      anything is saved. Works with files that don&apos;t match
                      the template.
                    </p>
                    <p className='text-center text-xs text-gray-500'>
                      Embedded images are imported when you approve.
                    </p>
                  </div>
                ) : (
                  <Button
                    intent='primary'
                    label={isUploadLoading ? 'Uploading...' : 'Upload file'}
                    startIcon={<MdUploadFile />}
                    onClick={handleUploadSKUExcel}
                    disabled={isUploadLoading}
                  />
                )}
                <Button
                  intent='danger'
                  label='Clear selected file'
                  startIcon={<MdDelete />}
                  onClick={() => {
                    setHasUploadError(false);
                    setUploadErrors([]);
                    setUploadErrorMessage(null);
                    setUploadFile(undefined);
                  }}
                />
                {hasUploadError && (
                  <div className='flex flex-col gap-2 w-full'>
                    <span className='text-red-600 font-semibold'>
                      Error uploading file.{' '}
                      {uploadErrors.length > 0
                        ? 'Please fix the following errors and try again.'
                        : 'Please try again.'}
                    </span>
                    {uploadErrorMessage && (
                      <span className='text-red-600'>{uploadErrorMessage}</span>
                    )}
                    {uploadErrors.map((error) => (
                      <span
                        key={`${error.row}-${error.message}`}
                        className='text-red-600'
                      >
                        Row {error.row}: {error.message}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Dropzone
                dropzoneId='sku-master-dropzone'
                label='Upload completed template'
                onDrop={(file) => {
                  setUploadFile(file);
                }}
                fileTypesTagline='.XLSX'
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

interface UploadSuccessPanelProps {
  summary: UploadSummary;
  onDone: () => void;
}

function UploadSuccessPanel({ summary, onDone }: UploadSuccessPanelProps) {
  const lines: string[] = [];
  if (summary.numberOfSkusCreated > 0) {
    lines.push(`${summary.numberOfSkusCreated} SKUs created`);
  }
  if (summary.numberOfExistingSkusUpdatedWithImages > 0) {
    lines.push(
      `${summary.numberOfExistingSkusUpdatedWithImages} existing SKUs updated with images`
    );
  }
  if (summary.numberOfImagesImported > 0) {
    lines.push(`${summary.numberOfImagesImported} images imported`);
  }
  if (lines.length === 0) {
    lines.push('No new SKUs were created');
  }

  return (
    <div className='flex flex-col items-center gap-6 w-full'>
      <MdCheckCircle size='3rem' className='text-green-600' />
      <span className='text-lg font-semibold text-gray-800'>Upload complete</span>
      <div className='flex flex-col gap-1 w-full items-center'>
        {lines.map((line) => (
          <span key={line} className='text-gray-700'>
            {line}
          </span>
        ))}
      </div>
      {summary.numberOfExistingSkusSkipped > 0 && (
        <p className='text-center text-xs text-gray-500'>
          {summary.numberOfExistingSkusSkipped} rows were skipped because those
          SKU IDs already exist — their data was not changed.
        </p>
      )}
      <Button intent='primary' label='Done' onClick={onDone} />
    </div>
  );
}
